import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { familiesApi } from './api';
import { ReadinessChecklist, type ReadinessItem } from './ReadinessChecklist';
import { FamilyProductsPanel } from './FamilyProductsPanel';
import { keys } from '@/lib/query/keys';
import { useAuth } from '@/lib/auth/useAuth';
import { useGate } from '@/lib/rbac/Gate';
import { useApiError } from '@/lib/query/useApiError';
import { useToast } from '@/lib/ui';
import { slugify } from '@/lib/slug';
import { diffPatch } from '@/lib/forms/diff';
import { useReferenceItems } from '@/lib/forms/widgets/ReferenceSelectField';
import { resolveI18n } from '@/lib/i18n/content';
import { useLocale } from '@/lib/i18n/useLocale';
import {
  requiredForReadyFields,
  isLocaleIncomplete,
  blockingProductSlugs,
} from '@/features/shared/governance';
import { ArabicCompleteControl } from '@/features/shared/ArabicCompleteControl';
import { DataView } from '@/lib/ui/states';
import { PageHeader } from '@/lib/ui/Typography';
import { Tabs } from '@/lib/ui/Tabs';
import { Card } from '@/lib/ui/Card';
import { TextInput } from '@/lib/ui/Field';
import { Select } from '@/lib/ui/Select';
import { Button, PermissionButton } from '@/lib/ui/Button';
import { ConfirmDialog } from '@/lib/ui/ConfirmDialog';
import { I18nField } from '@/lib/forms/widgets/I18nField';
import { MediaPickerField, EntityPickerField } from '@/lib/forms/widgets/PickerField';
import { SchemaForm } from '@/lib/forms/SchemaForm';
import { SeoPanel, EMPTY_SEO } from '@/features/shared/SeoPanel';
import { PublishControls } from '@/features/shared/PublishControls';
import { PreviewButton } from '@/features/preview/preview';
import { RevisionsTab } from '@/features/revisions/RevisionsTab';
import type { CatalogStatus, I18n, I18nRich, ProductFamily, SEO } from '@/lib/api/types';

const ENTITY_TYPE = 'family';

export function FamilyEditorPage() {
  const { id } = useParams();
  const isNew = id === 'new' || !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const onError = useApiError();
  const { isTranslateOnly } = useAuth();

  const { allowed: canWrite } = useGate('content:write');

  const query = useQuery({
    queryKey: keys.families.detail(id ?? 'new'),
    queryFn: () => familiesApi.get(id!),
    enabled: !isNew,
  });

  if (isNew) {
    return (
      <Editor
        original={null}
        canWrite={canWrite}
        translateOnly={isTranslateOnly}
        onSaved={(item) => navigate(`/families/${item.id}`)}
        onDeleted={() => navigate('/families')}
        qc={qc}
        toast={toast}
        onError={onError}
      />
    );
  }

  return (
    <DataView query={query}>
      {(item) => (
        <Editor
          original={item}
          canWrite={canWrite}
          translateOnly={isTranslateOnly}
          onSaved={() => qc.invalidateQueries({ queryKey: keys.families.detail(item.id) })}
          onDeleted={() => navigate('/families')}
          qc={qc}
          toast={toast}
          onError={onError}
        />
      )}
    </DataView>
  );
}

type EditorProps = {
  original: ProductFamily | null;
  canWrite: boolean;
  translateOnly: boolean;
  onSaved: (item: ProductFamily) => void;
  onDeleted: () => void;
  qc: ReturnType<typeof useQueryClient>;
  toast: ReturnType<typeof useToast>;
  onError: ReturnType<typeof useApiError>;
};

function Editor({ original, canWrite, translateOnly, onSaved, onDeleted, qc, toast, onError }: EditorProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const isNew = !original;

  // The rich-content (`doc`) form is generated from the backend's canonical schema
  // for product families, so the editor, server validation, and the public site all
  // share one shape (no hardcoded form to drift).
  const schemaQuery = useQuery({
    queryKey: keys.collectionSchemas(),
    queryFn: () => familiesApi.collectionSchemas(),
    staleTime: 60 * 60 * 1000,
  });
  const docSchema = schemaQuery.data?.product_family;

  const CATALOG_OPTIONS = [
    { value: 'preview', label: t('families.catalogPreview') },
    { value: 'ready', label: t('families.catalogReady') },
  ];

  const [name, setName] = useState<I18n>(original?.name ?? { en: '', ar: '' });
  const [shortName, setShortName] = useState<I18n>(original?.short_name ?? { en: '', ar: '' });
  const [tagline, setTagline] = useState<I18n>(original?.tagline ?? { en: '', ar: '' });
  const [summary, setSummary] = useState<I18nRich>(original?.summary ?? { en: '', ar: '' });
  const [description, setDescription] = useState<I18nRich>(original?.description ?? { en: '', ar: '' });
  const [catalogStatus, setCatalogStatus] = useState<CatalogStatus>(original?.catalog_status ?? 'preview');
  const [sortOrder, setSortOrder] = useState(original?.sort_order ?? 0);

  const [slug, setSlug] = useState(original?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(!!original);

  const [heroImageId, setHeroImageId] = useState<string | null>(original?.hero_image_id ?? null);
  const [iconId, setIconId] = useState<string | null>(original?.icon_id ?? null);
  const [schematicId, setSchematicId] = useState<string | null>(original?.schematic_id ?? null);
  const [datasheetId, setDatasheetId] = useState<string | null>(original?.datasheet_id ?? null);

  const [industryIds, setIndustryIds] = useState<string[]>(original?.industry_ids ?? []);

  const [doc, setDoc] = useState<Record<string, unknown>>((original?.doc as Record<string, unknown>) ?? {});
  const [seo, setSeo] = useState<SEO>(original?.seo ?? EMPTY_SEO);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBlockedBy, setDeleteBlockedBy] = useState<string[]>([]);

  // Resolve linked industry UUIDs → human names so the relationship is legible (no raw UUIDs).
  const industriesQuery = useReferenceItems('industries');
  const industryName = (entityId: string): string => {
    const item = (industriesQuery.data?.data ?? []).find((i) => i.id === entityId);
    return item ? resolveI18n(item.name ?? item.title, locale) || item.slug || entityId : entityId;
  };

  const arComplete = doc.ar_complete !== false; // absent ⇒ treated complete (legacy-safe)
  const setArComplete = (v: boolean) => setDoc((d) => ({ ...d, ar_complete: v }));

  const effectiveSlug = useMemo(
    () => (!slugTouched ? slugify(name.en) : slug),
    [slugTouched, name.en, slug],
  );

  // Readiness mirror of the backend gate (§4.2). The public page renders from the
  // `doc` blob, so "ready" means the renderable English content is present — the
  // index card and the detail page — not the legacy typed copy/media columns.
  const enContent = (block: unknown): boolean => {
    const en = (block as { en?: unknown } | undefined)?.en;
    return !!en && typeof en === 'object' && Object.keys(en as object).length > 0;
  };
  const readiness: ReadinessItem[] = [
    { key: 'name.en', labelKey: 'families.name', ok: !!name.en.trim() },
    { key: 'doc.card', labelKey: 'families.readyCard', ok: enContent(doc.card) },
    { key: 'doc.detail', labelKey: 'families.readyDetail', ok: enContent(doc.detail) },
  ];

  const addIndustry = (entityId: string | null) => {
    if (!entityId) return;
    setIndustryIds((prev) => (prev.includes(entityId) ? prev : [...prev, entityId]));
  };
  const removeIndustry = (entityId: string) =>
    setIndustryIds((prev) => prev.filter((x) => x !== entityId));

  const buildBody = (): Record<string, unknown> => ({
    slug: effectiveSlug,
    name,
    short_name: shortName,
    tagline,
    summary,
    description,
    catalog_status: catalogStatus,
    hero_image_id: heroImageId,
    icon_id: iconId,
    schematic_id: schematicId,
    datasheet_id: datasheetId,
    // Typed list columns are structured metadata the public site does not read (it
    // renders from `doc`). Preserve whatever exists rather than mirroring it out of
    // the doc, so the save diff never sends spurious clears.
    highlights: original?.highlights ?? [],
    applications: original?.applications ?? [],
    features: original?.features ?? [],
    standards: original?.standards ?? [],
    sort_order: sortOrder,
    seo,
    doc,
    industry_ids: industryIds,
  });

  const coverageOf = useMemo(
    () => ({ name, short_name: shortName, tagline, summary, description, doc }),
    [name, shortName, tagline, summary, description, doc],
  );

  const save = useMutation({
    mutationFn: async () => {
      setErrors({});
      if (isNew) return familiesApi.create(buildBody());
      const next = buildBody();
      const prev: Record<string, unknown> = {
        slug: original.slug,
        name: original.name,
        short_name: original.short_name,
        tagline: original.tagline,
        summary: original.summary,
        description: original.description,
        catalog_status: original.catalog_status,
        hero_image_id: original.hero_image_id,
        icon_id: original.icon_id,
        schematic_id: original.schematic_id,
        datasheet_id: original.datasheet_id,
        highlights: original.highlights,
        applications: original.applications,
        features: original.features,
        standards: original.standards,
        sort_order: original.sort_order,
        seo: original.seo,
        doc: original.doc,
        industry_ids: original.industry_ids,
      };
      return familiesApi.update(original.id, diffPatch(prev, next));
    },
    onSuccess: (item) => {
      void qc.invalidateQueries({ queryKey: keys.families.all() });
      void qc.invalidateQueries({ queryKey: keys.dashboard() });
      toast.success(isNew ? t('families.createdToast') : t('families.savedToast'), t('families.siteUpdateBody'));
      onSaved(item);
    },
    onError: (e) => {
      onError(e, { onValidation: setErrors });
      const missing = requiredForReadyFields(e);
      if (missing.length) toast.error(t('families.readyBlocked'), { description: t('families.readyBlockedDetail') });
    },
  });

  const publish = useMutation({
    mutationFn: (scheduledAt?: string) => familiesApi.publish(original!.id, scheduledAt),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.families.all() });
      toast.success(t('families.publishedToast'), t('families.siteUpdateBody'));
    },
    onError: (e) => handlePublishError(e, { onError, setErrors, toast, t }),
  });
  const unpublish = useMutation({
    mutationFn: () => familiesApi.unpublish(original!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.families.all() });
      toast.success(t('families.unpublishedToast'), t('families.unpublishRedirect'));
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: () => familiesApi.remove(original!.id),
    onSuccess: () => {
      qc.removeQueries({ queryKey: keys.families.detail(original!.id) });
      void qc.invalidateQueries({ queryKey: keys.families.all() });
      void qc.invalidateQueries({ queryKey: keys.dashboard() });
      setConfirmDelete(false);
      onDeleted();
    },
    onError: (e) => {
      const blocking = blockingProductSlugs(e);
      if (blocking.length) {
        setDeleteBlockedBy(blocking);
        setConfirmDelete(false);
      } else {
        onError(e);
      }
    },
  });

  const identityForm = (
    <Card>
      <div className="flex flex-col gap-md">
        <I18nField
          label={t('families.name')}
          value={name}
          onChange={setName}
          required
          error={errors['name.en'] ?? errors.name}
          translateOnly={translateOnly}
        />
        <I18nField label={t('families.shortName')} value={shortName} onChange={setShortName} translateOnly={translateOnly} />
        <I18nField
          label={t('families.tagline')}
          value={tagline}
          onChange={setTagline}
          error={errors['tagline.en']}
          translateOnly={translateOnly}
        />
        <TextInput
          label={t('common.slug')}
          value={effectiveSlug}
          disabled={translateOnly}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          error={errors.slug}
          hint={t('families.slugHint')}
        />
        <div className="flex flex-wrap gap-md">
          <div className="w-[200px]">
            <Select
              label={t('families.catalogStatus')}
              value={catalogStatus}
              onValueChange={(v) => setCatalogStatus(v as CatalogStatus)}
              options={CATALOG_OPTIONS}
              disabled={translateOnly}
              error={errors.catalog_status}
            />
          </div>
          <div className="w-[160px]">
            <TextInput
              type="number"
              label={t('families.sortOrder')}
              value={String(sortOrder)}
              disabled={translateOnly}
              onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
            />
          </div>
        </div>
        {catalogStatus === 'ready' && <ReadinessChecklist items={readiness} />}
      </div>
    </Card>
  );

  const contentForm = (
    <Card>
      <div className="flex flex-col gap-md">
        <I18nField label={t('families.summary')} value={summary} onChange={setSummary} multiline error={errors['summary.en']} translateOnly={translateOnly} />
        <I18nField
          label={t('families.description')}
          value={description}
          onChange={setDescription}
          multiline
          error={errors['description.en']}
          translateOnly={translateOnly}
        />
      </div>
    </Card>
  );

  const mediaForm = (
    <Card>
      <div className="flex flex-col gap-md">
        <MediaPickerField
          label={t('families.heroImage')}
          value={heroImageId}
          onChange={setHeroImageId}
          error={errors.hero_image_id}
          disabled={translateOnly}
        />
        <MediaPickerField label={t('families.icon')} value={iconId} onChange={setIconId} error={errors.icon_id} disabled={translateOnly} />
        <MediaPickerField
          label={t('families.schematic')}
          value={schematicId}
          onChange={setSchematicId}
          error={errors.schematic_id}
          disabled={translateOnly}
        />
        <MediaPickerField
          label={t('families.datasheet')}
          value={datasheetId}
          onChange={setDatasheetId}
          disabled={translateOnly}
        />
      </div>
    </Card>
  );

  const industriesForm = (
    <Card>
      <fieldset className="rounded-md border-[0.5px] border-hairline p-md">
        <legend className="eyebrow px-xs text-ink-mute">{t('families.industries')}</legend>
        <p className="mb-sm text-small text-ink-mute">{t('families.industriesHint')}</p>
        <div className="flex flex-col gap-sm">
          {industryIds.map((entityId) => (
            <div
              key={entityId}
              className="flex items-center justify-between gap-md rounded-sm border-[0.5px] border-hairline bg-canvas-sand px-sm py-xs"
            >
              <span className="text-small text-ink-deep">{industryName(entityId)}</span>
              {!translateOnly && (
                <Button type="button" variant="ghost" size="sm" onClick={() => removeIndustry(entityId)}>
                  {t('families.remove')}
                </Button>
              )}
            </div>
          ))}
          {industryIds.length === 0 && (
            <span className="text-small text-ink-mute">{t('families.noIndustries')}</span>
          )}
          <EntityPickerField
            label={t('families.addIndustry')}
            collection="industries"
            value={null}
            onChange={addIndustry}
            disabled={translateOnly}
          />
        </div>
      </fieldset>
    </Card>
  );

  const docForm = (
    <Card>
      {docSchema ? (
        <SchemaForm
          schema={docSchema}
          value={doc}
          onChange={setDoc}
          translateOnly={translateOnly}
          errors={errors}
          defs={docSchema.$defs}
        />
      ) : (
        <p className="text-small text-ink-mute">
          {schemaQuery.isError
            ? t('families.docSchemaError', { defaultValue: 'Could not load the content schema.' })
            : t('common.loading', { defaultValue: 'Loading…' })}
        </p>
      )}
    </Card>
  );

  const arForm = (
    <Card>
      <ArabicCompleteControl coverageOf={coverageOf} checked={arComplete} onChange={setArComplete} disabled={translateOnly} />
    </Card>
  );

  const productsForm = original ? (
    <Card>
      <FamilyProductsPanel familySlug={original.slug} familyId={original.id} />
    </Card>
  ) : null;

  const tabs = [
    {
      value: 'details',
      label: t('families.tabDetails'),
      content: (
        <div className="flex flex-col gap-lg">
          {identityForm}
          {contentForm}
          {mediaForm}
          {industriesForm}
          {docForm}
          {arForm}
        </div>
      ),
    },
    ...(productsForm
      ? [{ value: 'products', label: t('families.tabProducts'), content: productsForm }]
      : []),
    {
      value: 'seo',
      label: t('families.tabSeo'),
      content: (
        <Card>
          <SeoPanel value={seo} onChange={setSeo} translateOnly={translateOnly} />
        </Card>
      ),
    },
    ...(!isNew
      ? [
          {
            value: 'history',
            label: t('families.tabHistory'),
            content: <RevisionsTab entityType={ENTITY_TYPE} entityId={original.id} />,
          },
        ]
      : []),
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t('families.eyebrow')}
        title={isNew ? t('families.newTitle') : name.en || t('families.untitled')}
        actions={
          original ? <PreviewButton entityType={ENTITY_TYPE} entityId={original.id} /> : undefined
        }
      />

      {original && (
        <div className="mb-lg flex flex-wrap items-center justify-between gap-md">
          <PublishControls
            status={original.status}
            scheduledAt={original.scheduled_at}
            onPublish={(at) => publish.mutate(at)}
            onUnpublish={() => unpublish.mutate(undefined)}
            busy={publish.isPending || unpublish.isPending}
          />
          {canWrite && (
            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}>
              {t('common.delete')}
            </Button>
          )}
        </div>
      )}

      <Tabs tabs={tabs} />

      {/* Sticky action bar so Save stays reachable while editing a long form. */}
      <div className="sticky bottom-0 z-20 -mx-lg mt-lg flex items-center justify-end gap-sm border-t-[0.5px] border-hairline bg-canvas-sand/90 px-lg py-md backdrop-blur lg:-mx-xl lg:px-xl">
        <PermissionButton
          allowed={canWrite || translateOnly}
          reason={t('families.editPermission')}
          loading={save.isPending}
          onClick={() => save.mutate(undefined)}
        >
          {isNew ? t('common.saveDraft') : t('common.save')}
        </PermissionButton>
      </div>

      {original && (
        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title={t('families.deleteTitle')}
          description={t('families.deleteDescription')}
          confirmLabel={t('common.delete')}
          tone="danger"
          loading={remove.isPending}
          onConfirm={() => remove.mutate(undefined)}
        />
      )}

      {/* Block-and-warn delete cascade (§3.4): the family still has published products. */}
      <ConfirmDialog
        open={deleteBlockedBy.length > 0}
        onOpenChange={(o) => !o && setDeleteBlockedBy([])}
        title={t('families.deleteBlockedTitle')}
        description={t('families.deleteBlockedDescription', { slugs: deleteBlockedBy.join(', ') })}
        confirmLabel={t('common.ok')}
        onConfirm={() => setDeleteBlockedBy([])}
      />
    </div>
  );
}

/** Shared publish-error handling: map governance codes to clear, specific feedback. */
function handlePublishError(
  e: unknown,
  {
    onError,
    setErrors,
    toast,
    t,
  }: {
    onError: ReturnType<typeof useApiError>;
    setErrors: (fields: Record<string, string>) => void;
    toast: ReturnType<typeof useToast>;
    t: ReturnType<typeof useTranslation>['t'];
  },
) {
  if (isLocaleIncomplete(e)) {
    toast.error(t('governance.publishBlockedAr'), { description: t('governance.publishBlockedArDetail') });
    return;
  }
  const missing = requiredForReadyFields(e);
  if (missing.length) {
    onError(e, { onValidation: setErrors });
    toast.error(t('families.readyBlocked'), { description: t('families.readyBlockedDetail') });
    return;
  }
  onError(e);
}
